import { NavigationContainer } from '@react-navigation/native';
import { useFonts } from 'expo-font';
import ContextContainer from './context/ContextContainer';
import { ThemeProvider } from './context/ThemeContext';
import { BackgroundQueryProvider } from './context/BackgroundQueryContext';
import React, { useEffect } from 'react';
import { View, StyleSheet, Platform, Text } from 'react-native';
import LoadingScreen from './screens/LoadingScreen';
import SignInScreen from './screens/SignInScreen';
import MainPage from './MainPage';
import RegisterScreen from './screens/RegisterScreen';
import HomeScreen from './screens/HomeScreen';
import ExploreScreen from './screens/ExploreScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import ProfilePage from './screens/ProfilePage';
import SameGameParlay from './screens/SameGameParlay';
import SgpFlowScreen from './screens/SgpFlowScreen';
import UnifiedSearchScreen from './screens/UnifiedSearchScreen';
import BackgroundQueryModal from './components/BackgroundQueryModal';
import AdminEnvPill from './components/AdminEnvPill';
import FeedbackPill from './components/FeedbackPill';
// duplicate import removed
import PlansScreen from './screens/PlansScreen';
import SuspendedScreen from './screens/SuspendedScreen';
import SuccessScreen from './screens/SuccessScreen';
import AdminScreen from './AdminScreen';
// duplicate import removed
import { useNavigation } from './context/NavigationContext';
import { useLoading } from './context/LoadingContext';
import AdminLogin from './AdminLogin';
import CommunityScreen from './screens/CommunityScreen';
import DocsScreen from './screens/DocsScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import TermsScreen from './screens/TermsScreen';
import { FeedbackProvider } from './context/FeedbackContext';
import Toaster from './components/motion/Toaster';
import { SiteConfigProvider, useSiteConfig } from './context/SiteConfigContext';
import LegalFooter from './components/LegalFooter';
import AdminPreviewBanner from './components/AdminPreviewBanner';
import { SentryErrorBoundary } from './sentry';

// Font imports
const ClimateCrisisFont = require('./assets/fonts/ClimateCrisis-Regular-VariableFont_YEAR.ttf');
const SlateFont = require('./assets/fonts/SlateRg.TTF');
const ShantellSansFont = require('./assets/fonts/ShantellSans-VariableFont_BNCE,INFM,SPAC,wght.ttf');

function ScreenRouter() {
  const { currentScreen } = useNavigation();

  const renderScreen = () => {
    switch (currentScreen) {
      case 'MainPage':
        return <MainPage />;
      case 'Login':
        return <SignInScreen />;
      case 'Register':
        return <RegisterScreen />;
      case 'ForgotPassword':
        return <ForgotPasswordScreen />;
      case 'ResetPassword':
        return <ResetPasswordScreen />;
      case 'Home':
        return <HomeScreen />;
      case 'Dashboard':
        return <HomeScreen />;
      case 'Explore':
        return <ExploreScreen />;
      case 'Community':
        return <CommunityScreen />;
      case 'SameGameParlay':
        return <SameGameParlay />;
      case 'SgpFlow':
        return <SgpFlowScreen />;
      case 'UnifiedSearch':
        return <UnifiedSearchScreen />;
      case 'Profile':
        return <ProfilePage />; 
      case 'Plans':
        return <PlansScreen />;
      case 'Suspended':
        return <SuspendedScreen />;
      case 'Success':
        return <SuccessScreen />;
      case 'Admin':
        return <AdminScreen />;
      case 'AdminLogin':
        return <AdminLogin />;
      case 'Docs':
        return <DocsScreen />;
      case 'Onboarding':
        return <OnboardingScreen />;
      case 'Privacy':
        return <PrivacyScreen />;
      case 'Terms':
        return <TermsScreen />;
      default:
        return <HomeScreen />;
    }
  };

  return renderScreen();
}

function AppContent() {
  const { finishLoading, loadingDone } = useLoading();
  const { introSequenceEnabled } = useSiteConfig();

  useEffect(() => {
    if (!introSequenceEnabled && !loadingDone) {
      finishLoading();
    }
  }, [introSequenceEnabled, loadingDone, finishLoading]);

  return (
    <View style={styles.root}>
      <ScreenRouter />
      <View style={styles.legalFooterWrap}>
        <LegalFooter />
      </View>
      {/* Background Query Modal - floats in bottom right corner */}
      <AdminPreviewBanner />
      <BackgroundQueryModal />
      <FeedbackPill />
      <AdminEnvPill />
      {!loadingDone && introSequenceEnabled && (
        <View style={styles.loadingOverlay}>
          <LoadingScreen onFinish={finishLoading} />
        </View>
      )}
    </View>
  );
}

export default function App() {

  // ✅ FIX: Check if fonts are loaded before rendering
  const [fontsLoaded, fontError] = useFonts({
    ClimateCrisis: ClimateCrisisFont,
    Slate: SlateFont,
    ShantellSans: ShantellSansFont,
  });

  // Set title immediately on app load
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Bet Optimized';
    }
  }, []);

  // ✅ FIX: Wait for fonts to load (prevents mobile white screen)
  if (!fontsLoaded && !fontError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#000' }}>
        <Text style={{ color: '#fff' }}>Loading fonts...</Text>
      </View>
    );
  }

  // ✅ FIX: Show error if fonts fail to load
  if (fontError) {
    console.error('Font loading error:', fontError);
    // Still render app even if fonts fail (graceful degradation)
  }

  return (
    <SentryErrorBoundary
      fallback={({ error }) => (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#0a0a0f', padding: 20 }}>
          <Text style={{ color: '#fff', fontSize: 20, fontWeight: 'bold', marginBottom: 12 }}>
            Something went wrong
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
            The app encountered an unexpected error. Our team has been notified.
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center' }}
                numberOfLines={3}>
            {error?.message || 'Unknown error'}
          </Text>
        </View>
      )}
    >
    <NavigationContainer>
      <SiteConfigProvider>
        <ContextContainer>
          <ThemeProvider>
            <BackgroundQueryProvider>
            <FeedbackProvider>
            <AppContent />
            <Toaster />
            </FeedbackProvider>
            </BackgroundQueryProvider>
          </ThemeProvider>
        </ContextContainer>
      </SiteConfigProvider>
    </NavigationContainer>
    </SentryErrorBoundary>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  legalFooterWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    pointerEvents: 'box-none',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});